import { SiteContent, Review, Registration } from '../types';
import { INITIAL_CONTENT } from '../constants';
import { CONFIG } from '../config';

/**
 * Strips HTML tags and suspicious patterns from strings to prevent XSS.
 */
const sanitize = (val: any): any => {
  if (typeof val === 'string') {
    return val.replace(/<[^>]*>?/gm, '').trim();
  }
  if (Array.isArray(val)) {
    return val.map(sanitize);
  }
  if (val !== null && typeof val === 'object') {
    const obj: any = {};
    for (const key in val) {
      obj[key] = sanitize(val[key]);
    }
    return obj;
  }
  return val;
};

// Local storage keys
const LOCAL_KEYS = {
  INQUIRIES: 'balut_local_inquiries',
  REGISTRATIONS: 'balut_local_registrations',
  REVIEWS: 'balut_local_reviews',
  SITE_DATA: CONFIG.STORAGE_KEY
};

/**
 * Helper to merge cloud and local arrays with de-duplication
 */
function mergeAndDedupe(local: any[], cloud: any[], key: string = 'id') {
  const map = new Map();
  local.forEach(item => map.set(item[key], item));
  cloud.forEach(item => map.set(item[key], item));
  return Array.from(map.values());
}

/**
 * Supabase-powered Database Service with enhanced security and local persistence fallback.
 */
export const dbService = {
  /**
   * Ping the database to verify keys and connectivity
   */
  async checkConnection(): Promise<boolean> {
    if (!CONFIG.USE_REAL_BACKEND) return false;
    try {
      const response = await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/site_data?id=eq.1&select=id`, {
        method: 'GET',
        headers: {
          'apikey': CONFIG.SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`,
        }
      });
      return response.ok;
    } catch (e) {
      return false;
    }
  },

  /**
   * GET - Fetch Site Content
   */
  async fetchContent(): Promise<SiteContent> {
    if (CONFIG.USE_REAL_BACKEND) {
      try {
        const response = await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/site_data?id=eq.1&select=content`, {
          method: 'GET',
          headers: {
            'apikey': CONFIG.SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`,
            'Accept': 'application/json',
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) return data[0].content as SiteContent;
        }
      } catch (error) {
        console.warn('Cloud storage unavailable, switching to local.');
      }
    }
    const saved = localStorage.getItem(LOCAL_KEYS.SITE_DATA);
    return saved ? JSON.parse(saved) : INITIAL_CONTENT;
  },

  /**
   * POST - Update Site Content
   */
  async saveContent(content: SiteContent): Promise<{ success: boolean }> {
    localStorage.setItem(LOCAL_KEYS.SITE_DATA, JSON.stringify(content));
    if (CONFIG.USE_REAL_BACKEND) {
      try {
        const response = await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/site_data`, {
          method: 'POST',
          headers: { 
            'apikey': CONFIG.SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'resolution=merge-duplicates'
          },
          body: JSON.stringify({
            id: 1,
            content: content,
            updated_at: new Date().toISOString()
          }),
        });
        return { success: response.ok };
      } catch (error) {
        return { success: true }; 
      }
    }
    return { success: true };
  },

  /**
   * POST - Submit Contact Inquiry
   */
  async submitInquiry(data: any): Promise<{ success: boolean; message: string }> {
    const cleanData = sanitize(data);
    const newInquiry = {
      id: Date.now(),
      ...cleanData,
      created_at: new Date().toISOString()
    };

    // ALWAYS save to local
    const local = JSON.parse(localStorage.getItem(LOCAL_KEYS.INQUIRIES) || '[]');
    localStorage.setItem(LOCAL_KEYS.INQUIRIES, JSON.stringify([newInquiry, ...local]));

    if (CONFIG.USE_REAL_BACKEND) {
      try {
        const response = await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/inquiries`, {
          method: 'POST',
          headers: { 
            'apikey': CONFIG.SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newInquiry),
        });
        return { success: response.ok, message: response.ok ? 'Sent successfully.' : 'Saved locally.' };
      } catch (error) {
        return { success: true, message: 'Saved to local registry.' };
      }
    }
    return { success: true, message: 'Local Mode: Inquiry stored.' };
  },

  /**
   * POST - Submit Registration
   */
  async submitRegistration(data: any): Promise<{ success: boolean; message: string }> {
    const cleanData = sanitize(data);
    const newReg = {
      id: Date.now(),
      ...cleanData,
      created_at: new Date().toISOString()
    };

    const local = JSON.parse(localStorage.getItem(LOCAL_KEYS.REGISTRATIONS) || '[]');
    localStorage.setItem(LOCAL_KEYS.REGISTRATIONS, JSON.stringify([newReg, ...local]));

    if (CONFIG.USE_REAL_BACKEND) {
      try {
        const response = await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/registrations`, {
          method: 'POST',
          headers: { 
            'apikey': CONFIG.SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newReg),
        });
        return { success: response.ok, message: 'Registration logged.' };
      } catch (error) {
        return { success: true, message: 'Logged locally.' };
      }
    }
    return { success: true, message: 'Local Mode: Registration logged.' };
  },

  /**
   * REVIEWS SECTION
   */
  async fetchReviews(): Promise<Review[]> {
    let cloudReviews: Review[] = [];
    if (CONFIG.USE_REAL_BACKEND) {
      try {
        const response = await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/reviews?select=*&order=created_at.desc`, {
          method: 'GET',
          headers: { 'apikey': CONFIG.SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}` }
        });
        if (response.ok) cloudReviews = await response.json();
      } catch (err) {}
    }
    const localReviews = JSON.parse(localStorage.getItem(LOCAL_KEYS.REVIEWS) || '[]');
    return mergeAndDedupe(localReviews, cloudReviews).sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  },

  async submitReview(review: { name: string, rating: number, comment: string }): Promise<boolean> {
    const cleanReview = {
      id: Date.now(),
      ...sanitize(review),
      created_at: new Date().toISOString()
    };
    const local = JSON.parse(localStorage.getItem(LOCAL_KEYS.REVIEWS) || '[]');
    localStorage.setItem(LOCAL_KEYS.REVIEWS, JSON.stringify([cleanReview, ...local]));

    if (CONFIG.USE_REAL_BACKEND) {
      try {
        const response = await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/reviews`, {
          method: 'POST',
          headers: { 'apikey': CONFIG.SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(cleanReview),
        });
        return response.ok;
      } catch (err) { return true; }
    }
    return true;
  },

  async deleteReview(id: number): Promise<boolean> {
    const local = JSON.parse(localStorage.getItem(LOCAL_KEYS.REVIEWS) || '[]');
    localStorage.setItem(LOCAL_KEYS.REVIEWS, JSON.stringify(local.filter((r: any) => r.id !== id)));
    if (CONFIG.USE_REAL_BACKEND) {
      try {
        await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/reviews?id=eq.${id}`, {
          method: 'DELETE',
          headers: { 'apikey': CONFIG.SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}` }
        });
      } catch (err) {}
    }
    return true;
  },

  /**
   * INQUIRIES MANAGEMENT
   */
  async fetchInquiries(): Promise<any[]> {
    let cloudInquiries: any[] = [];
    if (CONFIG.USE_REAL_BACKEND) {
      try {
        const response = await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/inquiries?select=*&order=created_at.desc`, {
          method: 'GET',
          headers: { 'apikey': CONFIG.SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}` }
        });
        if (response.ok) cloudInquiries = await response.json();
      } catch (err) {}
    }
    const localInquiries = JSON.parse(localStorage.getItem(LOCAL_KEYS.INQUIRIES) || '[]');
    return mergeAndDedupe(localInquiries, cloudInquiries).sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  },

  async deleteInquiry(id: number): Promise<boolean> {
    const local = JSON.parse(localStorage.getItem(LOCAL_KEYS.INQUIRIES) || '[]');
    localStorage.setItem(LOCAL_KEYS.INQUIRIES, JSON.stringify(local.filter((i: any) => i.id !== id)));
    if (CONFIG.USE_REAL_BACKEND) {
      try {
        await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/inquiries?id=eq.${id}`, {
          method: 'DELETE',
          headers: { 'apikey': CONFIG.SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}` }
        });
      } catch (err) {}
    }
    return true;
  },

  /**
   * REGISTRATIONS MANAGEMENT
   */
  async fetchRegistrations(): Promise<Registration[]> {
    let cloudRegs: Registration[] = [];
    if (CONFIG.USE_REAL_BACKEND) {
      try {
        const response = await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/registrations?select=*&order=created_at.desc`, {
          method: 'GET',
          headers: { 'apikey': CONFIG.SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}` }
        });
        if (response.ok) cloudRegs = await response.json();
      } catch (err) {}
    }
    const localRegs = JSON.parse(localStorage.getItem(LOCAL_KEYS.REGISTRATIONS) || '[]');
    return mergeAndDedupe(localRegs, cloudRegs).sort((a, b) => 
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  },

  async deleteRegistration(id: number): Promise<boolean> {
    const local = JSON.parse(localStorage.getItem(LOCAL_KEYS.REGISTRATIONS) || '[]');
    localStorage.setItem(LOCAL_KEYS.REGISTRATIONS, JSON.stringify(local.filter((r: any) => r.id !== id)));
    if (CONFIG.USE_REAL_BACKEND) {
      try {
        await fetch(`${CONFIG.SUPABASE_URL}/rest/v1/registrations?id=eq.${id}`, {
          method: 'DELETE',
          headers: { 'apikey': CONFIG.SUPABASE_ANON_KEY, 'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}` }
        });
      } catch (err) {}
    }
    return true;
  }
};